/**
 * CDX Problem-Sets (CO-4661, Baustein 1)
 *
 * Mehrfachauswahl von Zubehör-Sets (eigene Produkte ohne Gerät). Alle gewählten Sets
 * werden zusammen mit der Zahnbürste in den Warenkorb gelegt und tragen Line-Item-
 * Properties, über die sie im Warenkorb als Unterposition erscheinen
 * (siehe snippets/line-item.liquid).
 *
 * Läuft neben "CDX Cross-Sell Abo": ist dort zusätzlich etwas ausgewählt, werden
 * diese Artikel im selben Request mitgeschickt.
 */

if (!customElements.get('cdx-problem-bundles')) {
  class CdxProblemBundles extends HTMLElement {
    #abortController = null;
    #selected = new Set();

    connectedCallback() {
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      this._form = document.forms[this.getAttribute('form')];
      this._summary = this.querySelector('[data-cdx-pb-summary]');
      this._summaryText = this.querySelector('[data-cdx-pb-summary-text]');
      this._summarySavings = this.querySelector('[data-cdx-pb-summary-savings]');
      this._savingsLabel = this.getAttribute('data-savings-label') || 'Sie sparen';

      this.querySelectorAll('input[data-cdx-pb-choice]').forEach((input) => {
        input.addEventListener('change', () => this._toggle(input.value, input.checked), { signal });
      });

      this.addEventListener('click', (event) => {
        const open = event.target.closest('[data-cdx-pb-modal-open]');
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          this._openModal(open.dataset.cdxPbModalOpen);
        }
      }, { signal });

      document.addEventListener('click', (event) => {
        const select = event.target.closest('[data-cdx-pb-modal-select]');
        if (select && this._ownsModal(select)) {
          const id = select.dataset.cdxPbModalSelect;
          this._setChecked(id, !this.#selected.has(id));
          this._closeModals();
          return;
        }
        if (event.target.closest('[data-cdx-pb-modal-close]') && this._ownsModal(event.target)) {
          this._closeModals();
          return;
        }
        const overlay = event.target.closest('[data-cdx-pb-modal]');
        if (overlay && event.target === overlay && this._ownsModal(overlay)) {
          this._closeModals();
        }
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this._closeModals();
      }, { signal });

      if (this._form) {
        document.addEventListener('submit', (event) => this._onSubmitCapture(event), { capture: true, signal });
      }

      this.querySelectorAll('input[data-cdx-pb-choice]:checked').forEach((input) => this.#selected.add(input.value));
      this._render();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this._closeModals();
    }

    /* ---------------- Auswahl (mehrfach) ---------------- */

    _setChecked(productId, checked) {
      const input = this.querySelector(`input[data-cdx-pb-choice][value="${productId}"]`);
      if (!input) return;
      input.checked = checked;
      this._toggle(productId, checked);
    }

    _toggle(productId, checked) {
      if (checked) this.#selected.add(productId);
      else this.#selected.delete(productId);
      this._render();
      this.dispatchEvent(new CustomEvent('cdx:problem-bundle:change', { bubbles: true, detail: { productIds: [...this.#selected] } }));
    }

    _render() {
      let savings = 0;
      this.querySelectorAll('[data-cdx-pb-card]').forEach((card) => {
        const active = this.#selected.has(card.dataset.cdxPbCard);
        card.classList.toggle('is-active', active);
        if (active) savings += parseInt(card.dataset.savings || '0', 10);
        const modalButton = this.querySelector(`[data-cdx-pb-modal-select="${card.dataset.cdxPbCard}"]`) || document.querySelector(`[data-cdx-pb-modal-select="${card.dataset.cdxPbCard}"]`);
        if (modalButton) {
          modalButton.textContent = active ? (this.getAttribute('data-deselect-label') || 'Set wieder abwählen') : (this.getAttribute('data-select-label') || 'Dieses Set dazu wählen');
          modalButton.classList.toggle('is-selected', active);
        }
      });
      const count = this.#selected.size;
      if (this._summary) {
        this._summary.classList.toggle('is-visible', count > 0);
        if (this._summarySavings) {
          this._summarySavings.textContent = savings > 0 ? `${this._savingsLabel} ${this._formatMoney(savings)}` : '';
        }
      }
    }

    get selectedCards() {
      return [...this.#selected].map((id) => this.querySelector(`[data-cdx-pb-card="${id}"]`)).filter(Boolean);
    }

    /* ---------------- Modal ---------------- */

    _ownsModal(element) {
      const overlay = element.closest('[data-cdx-pb-modal]');
      return overlay && overlay._cdxOwner === this;
    }

    _openModal(productId) {
      const overlay = this.querySelector(`[data-cdx-pb-modal="${productId}"]`) || document.querySelector(`[data-cdx-pb-modal="${productId}"]`);
      if (!overlay) return;
      overlay._cdxOwner = this;
      document.body.appendChild(overlay);
      overlay.offsetHeight;
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    _closeModals() {
      document.querySelectorAll('[data-cdx-pb-modal].is-open').forEach((overlay) => {
        if (overlay._cdxOwner !== this) return;
        overlay.classList.remove('is-open');
        this.querySelector('.cdx-pb__rows')?.appendChild(overlay);
      });
      if (!document.querySelector('[data-cdx-pb-modal].is-open, [data-cdx-cs-modal].is-open')) {
        document.body.style.overflow = '';
      }
    }

    /* ---------------- Warenkorb ---------------- */

    _onSubmitCapture(event) {
      if (event.target !== this._form) return;
      const cards = this.selectedCards;
      if (cards.length === 0) return; // keine Sets gewählt → normaler Ablauf
      event.preventDefault();
      event.stopImmediatePropagation();
      this._addWithSets(cards);
    }

    async _addWithSets(cards) {
      const form = this._form;
      const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
      submitButtons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));

      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));

      const formData = new FormData(form);
      const mainVariantId = parseInt(formData.get('id'), 10);
      const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;
      const parentTitle = this.getAttribute('data-parent-title') || '';
      const propertyLabel = this.getAttribute('data-property-label') || 'Zubehör-Set für';

      const mainProperties = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && value) mainProperties[key.slice(11, -1)] = value;
      }
      const mainItem = { id: mainVariantId, quantity };
      if (Object.keys(mainProperties).length) mainItem.properties = mainProperties;
      const mainSellingPlan = formData.get('selling_plan');
      if (mainSellingPlan) mainItem.selling_plan = parseInt(mainSellingPlan, 10);

      const items = [mainItem];
      cards.forEach((card) => {
        const setProperties = {
          _cdx_set_parent_variant: String(mainVariantId),
          _cdx_set_parent_title: parentTitle,
        };
        setProperties[propertyLabel] = parentTitle;
        items.push({ id: parseInt(card.dataset.variantId, 10), quantity, properties: setProperties });
      });

      // Auswahl aus "CDX Cross-Sell Abo" (gleiches Formular) mitnehmen
      const crossSell = document.querySelector(`cdx-cross-sell-subscription[form="${form.id}"]`);
      if (crossSell && typeof crossSell.getSelectedCrossSells === 'function') {
        const bundleId = `cdx_${Date.now()}`;
        crossSell.getSelectedCrossSells().forEach((cs) => {
          const item = {
            id: parseInt(cs.variantId, 10),
            quantity: 1,
            properties: {
              _cdx_bundle: bundleId,
              _cdx_bundle_child: 'true',
              _cdx_parent_product: parentTitle,
              _cdx_parent_product_id: this.getAttribute('data-parent-id') || '',
            },
          };
          if (cs.sellingPlanId) item.selling_plan = parseInt(cs.sellingPlanId, 10);
          items.push(item);
        });
      }

      const payload = {
        items,
        sections: sectionsToBundle.join(','),
        sections_url: `${Shopify.routes.root}variants/${mainVariantId}`,
      };

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });
        submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
        const responseJson = await response.json();

        if (response.ok) {
          if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
            window.location.href = `${Shopify.routes.root}cart`;
            return;
          }
          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          cartContent.sections = responseJson.sections;
          form.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: responseJson.items || [responseJson], cart: cartContent },
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent },
          }));
        } else {
          form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.description } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (error) {
        submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
        console.error('[CDX Problem-Sets] Cart add failed:', error);
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.' },
        }));
      }
    }

    _formatMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  customElements.define('cdx-problem-bundles', CdxProblemBundles);
}
